'use client';

import useSWRImmutable from 'swr/immutable';
import { EventsCarouselSkeleton } from '@v2/components/EventsCarouselSkeleton/EventsCarouselSkeleton';
import { getTeamPackages, TeamScheduleQuery } from '@v2/services/team-schedule';
import { formatLeagueSlug, getVenueImages } from '@v2/services/getVenueImages';
import { Button } from '@v2/ui/Button';
import { TeamFeaturedPackages } from '../components/TeamFeaturedPackages';
import { getTeamGameHref } from '../util/teamSchedule';

const fetchFeaturedPackages = async (query: TeamScheduleQuery) => {
  const response = await getTeamPackages(query);
  const league = formatLeagueSlug(query.league);
  const images = response.packages.length
    ? await getVenueImages(response.packages, league)
    : [];
  const imagesByVenue = new Map<string, string>(
    (Array.isArray(images) ? images : []).map((image) => [
      `${image.venueId}:${image.league}`,
      image.imagePath,
    ])
  );

  return {
    ...response,
    packages: response.packages.map((item) => {
      const image = imagesByVenue.get(`${item.venue.id}:${league}`);
      return {
        ...item,
        imageUrl:
          image && !image.includes('/default/600x400-generic-event.jpg')
            ? image
            : '/default-event-image.svg',
        href: getTeamGameHref(item),
      };
    }),
  };
};

export function TeamFeaturedPackagesWrapper({
  league,
  teamSlug,
}: TeamScheduleQuery) {
  const { data, error, mutate } = useSWRImmutable(
    `team-packages:${league}:${teamSlug}`,
    () => fetchFeaturedPackages({ league, teamSlug }),
    // The API returns 503 on a transient upstream (Tevo) failure; auto-retry so a blip heals without a click.
    { errorRetryCount: 3, errorRetryInterval: 1000 }
  );

  if (error) {
    return (
      <section role="alert" className="rounded-xl border border-gray-200 p-6">
        <p className="mb-3 text-sm text-gray-600">
          We couldn&apos;t load upcoming packages. Please try again.
        </p>
        <Button variant="secondary" onClick={() => void mutate()}>
          Try packages again
        </Button>
      </section>
    );
  }

  if (!data) {
    return (
      <div role="status" aria-label="Loading upcoming packages">
        <EventsCarouselSkeleton />
      </div>
    );
  }

  return (
    <TeamFeaturedPackages teamName={data.team.name} packages={data.packages} />
  );
}
